"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toReactHookForm = toReactHookForm;
exports.createValidator = createValidator;
/**
 * Converts a validation schema to React Hook Form validation rules
 */
function toReactHookForm(schema, fieldMapping) {
    const validation = {};
    Object.entries(schema.fields).forEach(([fieldName, rule]) => {
        const uiFieldName = fieldMapping?.[fieldName] || fieldName;
        const fieldMessages = schema.messages[fieldName] || {};
        validation[uiFieldName] = convertRuleToReactHookForm(rule, fieldMessages);
    });
    return validation;
}
/**
 * Converts a single validation rule to React Hook Form format
 */
function convertRuleToReactHookForm(rule, messages) {
    const validation = {};
    if (rule.required) {
        validation.required = messages.required || 'This field is required';
    }
    if (rule.minLength) {
        validation.minLength = {
            value: rule.minLength,
            message: messages.minLength || `Must be at least ${rule.minLength} characters`,
        };
    }
    if (rule.maxLength) {
        validation.maxLength = {
            value: rule.maxLength,
            message: messages.maxLength || `Must be less than ${rule.maxLength} characters`,
        };
    }
    if (rule.exactLength) {
        validation.minLength = {
            value: rule.exactLength,
            message: messages.exactLength || `Must be exactly ${rule.exactLength} characters`,
        };
        validation.maxLength = {
            value: rule.exactLength,
            message: messages.exactLength || `Must be exactly ${rule.exactLength} characters`,
        };
    }
    if (rule.min !== undefined) {
        validation.min = {
            value: rule.min,
            message: messages.min || `Must be at least ${rule.min}`,
        };
    }
    if (rule.max !== undefined) {
        validation.max = {
            value: rule.max,
            message: messages.max || `Must be no more than ${rule.max}`,
        };
    }
    if (rule.email) {
        validation.pattern = {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: messages.email || 'Invalid email address',
        };
    }
    if (rule.pattern) {
        validation.pattern = {
            value: rule.pattern,
            message: messages.pattern || 'Invalid format',
        };
    }
    if (rule.dateFormat) {
        const dateRegex = rule.dateFormat.format === 'YYYY-MM-DD'
            ? /^\d{4}-\d{2}-\d{2}$/
            : new RegExp(rule.dateFormat.format);
        validation.pattern = {
            value: dateRegex,
            message: rule.dateFormat.message ||
                messages.dateFormat ||
                `Date must be in ${rule.dateFormat.format} format`,
        };
    }
    // Combine all custom validations into a single validate function
    const validators = [];
    if (rule.custom) {
        validators.push((value) => rule.custom.validate(value) || rule.custom.message);
    }
    if (rule.conditionalRequired) {
        validators.push((value, formValues) => {
            if (rule.conditionalRequired.condition(formValues)) {
                if (value === undefined || value === null || value === '') {
                    return (rule.conditionalRequired.message ||
                        messages.conditionalRequired ||
                        'This field is required');
                }
            }
            return true;
        });
    }
    if (rule.crossFieldValidation) {
        validators.push((value, formValues) => {
            return rule.crossFieldValidation.validate(value, formValues);
        });
    }
    if (rule.dateFormat && rule.dateFormat.format === 'YYYY-MM-DD') {
        validators.push((value) => {
            if (!value)
                return true; // Let required validation handle empty values
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(value))
                return false;
            const date = new Date(value);
            const isValidDate = date instanceof Date &&
                !isNaN(date.getTime()) &&
                value === date.toISOString().split('T')[0];
            return (isValidDate || rule.dateFormat.message || messages.dateFormat || 'Invalid date format');
        });
    }
    if (validators.length > 0) {
        validation.validate = (value) => {
            // For React Hook Form, we need to access form values through getValues
            // This is a limitation - cross-field validation in RHF requires different approach
            for (const validator of validators) {
                const result = typeof validator === 'function' && validator.length > 1
                    ? validator(value, {}) // Pass empty object for now - RHF cross-field validation needs different implementation
                    : validator(value);
                if (result !== true) {
                    return result;
                }
            }
            return true;
        };
    }
    return validation;
}
/**
 * Creates a validation function for runtime validation with enhanced cross-field support
 */
function createValidator(schema) {
    return (data) => {
        const errors = {};
        let isValid = true;
        Object.entries(schema.fields).forEach(([fieldName, rule]) => {
            const value = data[fieldName];
            const fieldErrors = [];
            const fieldMessages = schema.messages[fieldName] || {};
            // Conditional required validation
            if (rule.conditionalRequired && rule.conditionalRequired.condition(data)) {
                if (value === undefined || value === null || value === '') {
                    fieldErrors.push(rule.conditionalRequired.message ||
                        fieldMessages.conditionalRequired ||
                        'This field is required');
                }
            }
            // Standard required validation (if not conditionally required)
            if (rule.required &&
                !rule.conditionalRequired &&
                (value === undefined || value === null || value === '')) {
                fieldErrors.push(fieldMessages.required || 'This field is required');
            }
            // Cross-field validation - must run even for empty values
            if (rule.crossFieldValidation) {
                const crossFieldResult = rule.crossFieldValidation.validate(value, data);
                if (crossFieldResult !== true) {
                    fieldErrors.push(typeof crossFieldResult === 'string' ? crossFieldResult : 'Validation failed');
                }
            }
            // Skip other validations if value is empty (regardless of required status)
            if (value === undefined || value === null || value === '') {
                if (fieldErrors.length > 0) {
                    errors[fieldName] = fieldErrors;
                    isValid = false;
                }
                return;
            }
            // Length validations
            if (rule.minLength && value.length < rule.minLength) {
                fieldErrors.push(fieldMessages.minLength || `Must be at least ${rule.minLength} characters`);
            }
            if (rule.maxLength && value.length > rule.maxLength) {
                fieldErrors.push(fieldMessages.maxLength || `Must be less than ${rule.maxLength} characters`);
            }
            if (rule.exactLength && value.length !== rule.exactLength) {
                fieldErrors.push(fieldMessages.exactLength || `Must be exactly ${rule.exactLength} characters`);
            }
            // Numeric validations
            if (rule.min !== undefined && value < rule.min) {
                fieldErrors.push(fieldMessages.min || `Must be at least ${rule.min}`);
            }
            if (rule.max !== undefined && value > rule.max) {
                fieldErrors.push(fieldMessages.max || `Must be no more than ${rule.max}`);
            }
            // Email validation
            if (rule.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                fieldErrors.push(fieldMessages.email || 'Invalid email address');
            }
            // Pattern validation
            if (rule.pattern && !rule.pattern.test(value)) {
                fieldErrors.push(fieldMessages.pattern || 'Invalid format');
            }
            // Date format validation
            if (rule.dateFormat) {
                if (rule.dateFormat.format === 'YYYY-MM-DD') {
                    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                    if (!dateRegex.test(value)) {
                        fieldErrors.push(rule.dateFormat.message ||
                            fieldMessages.dateFormat ||
                            'Date must be in YYYY-MM-DD format');
                    }
                    else {
                        const date = new Date(value);
                        const isValidDate = date instanceof Date &&
                            !isNaN(date.getTime()) &&
                            value === date.toISOString().split('T')[0];
                        if (!isValidDate) {
                            fieldErrors.push(rule.dateFormat.message || fieldMessages.dateFormat || 'Invalid date');
                        }
                    }
                }
            }
            // Custom validation
            if (rule.custom) {
                const customResult = rule.custom.validate(value);
                if (!customResult) {
                    fieldErrors.push(rule.custom.message);
                }
            }
            if (fieldErrors.length > 0) {
                errors[fieldName] = fieldErrors;
                isValid = false;
            }
        });
        return { isValid, errors };
    };
}
